import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, Clock, Video, FileText, Play, ChevronRight } from 'lucide-react';
import PDFViewer from '@/components/PDFViewer';
import { API } from '@/App';

const CATEGORY_COLORS = {
  'Les Rapports': { bg: 'from-amber-500 to-orange-600', accent: '#f59e0b', text: 'text-amber-700', bgLight: 'bg-amber-50' },
  'Les Publications': { bg: 'from-emerald-500 to-teal-600', accent: '#10b981', text: 'text-emerald-700', bgLight: 'bg-emerald-50' },
  'Les Cours': { bg: 'from-blue-500 to-indigo-600', accent: '#3b82f6', text: 'text-blue-700', bgLight: 'bg-blue-50' },
  'Les Replays': { bg: 'from-violet-500 to-purple-600', accent: '#8b5cf6', text: 'text-violet-700', bgLight: 'bg-violet-50' },
  'Grand Prix': { bg: 'from-rose-500 to-pink-600', accent: '#f43f5e', text: 'text-rose-700', bgLight: 'bg-rose-50' },
};

const FormationDetailPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [formation, setFormation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeEpisode, setActiveEpisode] = useState(0);

  useEffect(() => {
    fetchFormation();
  }, [id]);

  const fetchFormation = async () => {
    try {
      const response = await axios.get(`${API}/formations/detail/${id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setFormation(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement de la formation');
      navigate('/formations');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#35AFE4]" />
      </div>
    );
  }

  if (!formation) return null;

  const catColor = CATEGORY_COLORS[formation.category] || { bg: 'from-gray-400 to-gray-500', accent: '#6b7280', text: 'text-gray-700', bgLight: 'bg-gray-50' };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="formation-detail-page">
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4 py-5">
          <Button variant="ghost" onClick={() => navigate('/formations')} className="mb-3 text-gray-500 hover:text-gray-900" data-testid="back-btn">
            <ArrowLeft className="mr-2" size={16} /> Retour aux formations
          </Button>
          <div className="flex items-center gap-2 mb-3">
            <span className={`px-3 py-1 ${catColor.bgLight} ${catColor.text} text-xs font-bold rounded-full`} data-testid="category">
              {formation.category}
            </span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2" style={{ fontFamily: "'Barlow', sans-serif" }} data-testid="title">
            {formation.title}
          </h1>
          <div className="flex items-center gap-3 text-gray-400 text-xs">
            {formation.instructor && (
              <span className="flex items-center gap-1.5">
                <div className={`w-5 h-5 rounded-full bg-gradient-to-br ${catColor.bg} flex items-center justify-center text-[8px] text-white font-bold`}>
                  {formation.instructor[0]}
                </div>
                {formation.instructor}
              </span>
            )}
            <span className="flex items-center gap-1" data-testid="created-date">
              <Clock size={12} /> {new Date(formation.created_at).toLocaleDateString('fr-FR')}
            </span>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">
        {formation.description && (
          <Card className="border border-gray-100 shadow-sm">
            <CardContent className="p-5">
              <p className="text-gray-600 text-sm leading-relaxed" data-testid="description">{formation.description}</p>
            </CardContent>
          </Card>
        )}

        {/* Episodes (Replays) */}
        {formation.episodes && formation.episodes.length > 0 && (
          <div className="space-y-4" data-testid="episodes-section">
            {/* Video Player */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100">
              <div className="aspect-video bg-gray-900">
                {(() => {
                  const ep = formation.episodes[activeEpisode];
                  if (!ep) return null;
                  const videoUrl = ep.video_url || '';
                  const isMp4 = videoUrl.endsWith('.mp4') || videoUrl.includes('.mp4');
                  const isYoutube = videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be');
                  if (isYoutube) {
                    const ytId = ep.youtube_id || videoUrl.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/)?.[1] || '';
                    return <iframe src={`https://www.youtube.com/embed/${ytId}?rel=0`} className="w-full h-full" allowFullScreen title={ep.title} />;
                  }
                  if (isMp4) {
                    return <video key={ep.id || activeEpisode} controls controlsList="nodownload" onContextMenu={e => e.preventDefault()} className="w-full h-full" autoPlay data-testid="video-player"><source src={videoUrl} type="video/mp4" /></video>;
                  }
                  return <div className="flex items-center justify-center h-full text-white/60">Format non supporte</div>;
                })()}
              </div>
              <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between">
                <div>
                  <p className="font-bold text-gray-900 text-sm">{formation.episodes[activeEpisode]?.title}</p>
                  <p className="text-gray-400 text-xs mt-0.5">Episode {activeEpisode + 1} sur {formation.episodes.length}{formation.episodes[activeEpisode]?.duration ? ` · ${formation.episodes[activeEpisode].duration}` : ''}</p>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => setActiveEpisode(Math.max(0, activeEpisode - 1))} disabled={activeEpisode === 0} className="p-2 rounded-full hover:bg-gray-100 disabled:opacity-20 text-gray-600"><ChevronRight size={16} className="rotate-180" /></button>
                  <button onClick={() => setActiveEpisode(Math.min(formation.episodes.length - 1, activeEpisode + 1))} disabled={activeEpisode >= formation.episodes.length - 1} className="p-2 rounded-full hover:bg-gray-100 disabled:opacity-20 text-gray-600"><ChevronRight size={16} /></button>
                </div>
              </div>
            </div>

            {/* Episode List */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="px-5 py-3 border-b border-gray-100">
                <h3 className="text-sm font-bold text-gray-900" style={{ fontFamily: "'Barlow', sans-serif" }}>{formation.episodes.length} episodes</h3>
              </div>
              <div className="divide-y divide-gray-50 max-h-[400px] overflow-y-auto">
                {[...formation.episodes].sort((a, b) => (a.order || 0) - (b.order || 0)).map((ep, i) => (
                  <button key={ep.id || i} onClick={() => setActiveEpisode(i)}
                    className={`w-full text-left px-4 py-3 flex items-center gap-4 transition-all ${activeEpisode === i ? 'bg-[#eaf6fc]' : 'hover:bg-gray-50'}`}
                    data-testid={`episode-${i}`}>
                    <span className={`text-sm font-bold w-5 text-center ${activeEpisode === i ? 'text-gray-900' : 'text-gray-300'}`}>{i + 1}</span>
                    <div className={`relative flex-shrink-0 w-20 h-12 rounded-lg overflow-hidden ${activeEpisode === i ? 'ring-2' : ''}`} style={activeEpisode === i ? { '--tw-ring-color': catColor.accent } : {}}>
                      <div className={`absolute inset-0 bg-gradient-to-br ${catColor.bg} flex items-center justify-center`}>
                        {activeEpisode === i ? (
                          <div className="flex gap-0.5 items-end"><div className="w-0.5 h-2 bg-white rounded-full animate-pulse" /><div className="w-0.5 h-3 bg-white rounded-full animate-pulse" style={{ animationDelay: '0.15s' }} /><div className="w-0.5 h-2 bg-white rounded-full animate-pulse" style={{ animationDelay: '0.3s' }} /></div>
                        ) : <Play size={12} className="text-white ml-0.5" fill="white" />}
                      </div>
                      {ep.duration && <span className="absolute bottom-0.5 right-0.5 px-1 py-px bg-black/60 text-white text-[8px] font-mono rounded">{ep.duration}</span>}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className={`text-sm leading-snug line-clamp-1 ${activeEpisode === i ? 'font-bold text-gray-900' : 'text-gray-600'}`}>{ep.title}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        {ep.duration && <span className="text-[10px] text-gray-400 flex items-center gap-0.5"><Clock size={9} /> {ep.duration}</span>}
                        {activeEpisode === i && <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: catColor.accent }}>En cours</span>}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Single Video */}
        {!formation.episodes?.length && formation.video_url && (
          <Card className="border border-gray-100 shadow-sm" data-testid="video-section">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <Video size={18} className="text-[#35AFE4]" />
                <h2 className="text-base font-bold text-gray-900">Video</h2>
              </div>
              <div className="aspect-video bg-gray-100 rounded-xl overflow-hidden">
                {formation.video_url.includes('youtube') || formation.video_url.includes('youtu.be') ? (
                  <iframe src={formation.video_url.replace('watch?v=', 'embed/')} className="w-full h-full" allowFullScreen title="Video" />
                ) : (
                  <video controls controlsList="nodownload" className="w-full h-full"><source src={formation.video_url} type="video/mp4" /></video>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* PDF */}
        {formation.pdf_url && (
          <div data-testid="pdf-section">
            <PDFViewer url={formation.pdf_url} title={formation.title} />
          </div>
        )}

        {/* Content */}
        {formation.content && formation.content.trim() !== '' && (
          <Card className="border border-gray-100 shadow-sm">
            <CardContent className="p-5">
              <div className="prose max-w-none text-gray-600 text-sm" data-testid="content" dangerouslySetInnerHTML={{ __html: formation.content }} />
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default FormationDetailPage;
